import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { useState } from "react";

import { LayoutSimple } from "~/components/layouts/simple";
import { Button } from "~/components/primitives/button";
import { Dialog } from "~/components/primitives/dialog";
import { ElevatedBox } from "~/components/primitives/elevated-box";
import { textInputStyles } from "~/components/primitives/input";
import { useFeatureFlag } from "~/lib/feature-flags/hooks";
import { deleteAccountMutation } from "~/modules/account/mutations";
import { UserProfileType } from "~/modules/auth/network";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/_app/settings/profile")({
  component: RouteComponent,
  loader({ context: { user } }) {
    return { user };
  },
});

function RouteComponent() {
  const { user } = Route.useLoaderData();

  const leaveTeamEnabled = false; // future feature flag
  const { enabled: accountDeletionEnabled } = useFeatureFlag("studio_enable_delete_account");

  return (
    <LayoutSimple title={t("mirage-studio:profile.title")}>
      <ElevatedBox.Root withDividers>
        <ElevatedBox.Item className={css({ justifyContent: "space-between" })}>
          <p className={css({ textStyle: "body-2-intense" })}>
            {t("mirage-studio:profile.full-name")}
          </p>
          {/* TODO: add input once endpoints exist */}
          <p className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}>{user.name}</p>
        </ElevatedBox.Item>
        <ElevatedBox.Item className={css({ justifyContent: "space-between" })}>
          <p className={css({ textStyle: "body-2-intense" })}>{t("mirage-studio:profile.email")}</p>
          <p className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}>{user.email}</p>
        </ElevatedBox.Item>
      </ElevatedBox.Root>
      {(leaveTeamEnabled || accountDeletionEnabled) && (
        <ElevatedBox.Root>
          {/* "Leave team" item will go here */}
          {accountDeletionEnabled && (
            <ElevatedBox.Item className={css({ justifyContent: "space-between" })}>
              <p className={css({ textStyle: "body-2-intense" })}>
                {t("mirage-studio:profile.delete-account")}
              </p>
              <DeleteAccountDialog
                user={user}
                trigger={
                  <Button variant="secondary-destructive" size="md">
                    {t("mirage-studio:profile.delete-account")}
                  </Button>
                }
              />
            </ElevatedBox.Item>
          )}
        </ElevatedBox.Root>
      )}
    </LayoutSimple>
  );
}

interface DeleteAccountDialogProps {
  trigger: React.ReactNode;
  user: UserProfileType;
}

function DeleteAccountDialog({ trigger, user }: DeleteAccountDialogProps) {
  const [email, setEmail] = useState("");
  const isValid = email === user.email;

  const [isOpen, setOpen] = useState(false);
  const onOpenChange = (open: boolean) => {
    setOpen(open);
    setEmail("");
  };

  const { mutate, isPending, isError } = deleteAccountMutation.useMutation();
  const deleteAccount = () => mutate({ params: {} });

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Content
        className={css({
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          maxWidth: 418,
        })}
      >
        <Dialog.Title className={css({ textStyle: "body-1-subtle", color: "$gray.200" })}>
          {t("mirage-studio:profile.delete-account-dialog.title")}
        </Dialog.Title>
        <Dialog.Description className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}>
          {t("mirage-studio:profile.delete-account-dialog.description")}
        </Dialog.Description>
        <p className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}>
          {t("mirage-studio:profile.delete-account-dialog.instructions")}
        </p>
        <input
          type="text"
          placeholder={user.email}
          className={textInputStyles}
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
        />
        {isError && (
          <span className={css({ color: "$error" })}>
            {t("mirage-studio:profile.delete-account-dialog.error")}
          </span>
        )}
        <div className={css({ display: "flex", gap: 8, justifyContent: "flex-end" })}>
          <Dialog.Close asChild>
            <Button variant="secondary" size="md">
              {t("common:cancel")}
            </Button>
          </Dialog.Close>

          <Button
            variant="destructive"
            disabled={!isValid}
            onClick={deleteAccount}
            isPending={isPending}
            size="md"
          >
            {t("mirage-studio:profile.delete-account")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
